import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { useVisibleRefresh } from "./useVisibleRefresh";

// The live ranking, shared by the ranking page and the Overview card.

// Both sources index minute-of-day at UTC+3 (epglogsreader fixedTZ = 3,
// YoutubeTrackerGrid BASE_OFFSET = 3), so "now" is measured in that frame
// rather than the browser's.
export const PANEL_TZ_OFFSET_HOURS = 3;

// How stale the last recorded minute may be before a stream counts as off air.
export const LIVE_TOLERANCE_MINUTES = 10;

export const RANKING_POLL_MS = 30000;
const MAX_CHANNELS = 20;

// Both endpoints are batched; they disagree about the separator.
const LIVE_SEPARATOR = "-";
const TRACKER_SEPARATOR = ",";

export function currentPanelMinute() {
  const now = new Date();
  const utcMinute = now.getUTCHours() * 60 + now.getUTCMinutes();
  return (((utcMinute + PANEL_TZ_OFFSET_HOURS * 60) % 1440) + 1440) % 1440;
}

// The current date in the same UTC+3 frame the minute indexes use, so "is the
// selected day today" cannot disagree with "which minute is it now".
export function panelTodayKey() {
  const shifted = new Date(Date.now() + PANEL_TZ_OFFSET_HOURS * 60 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
}

export function hhmm(minute) {
  if (minute === null || minute === undefined) return "";
  const h = String(Math.floor(minute / 60)).padStart(2, "0");
  const m = String(minute % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function storedUser() {
  try {
    return JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  } catch {
    return {};
  }
}

// PLACEHOLDER SOURCE: borrows Inteltube's list until the board gets one of its
// own. Everything downstream works off whatever this returns.
export function candidateChannelIds(currentChannelId) {
  const u = storedUser();
  const pool =
    Array.isArray(u.ytchannels) && u.ytchannels.length > 0 ? u.ytchannels : u.channels || [];

  const ids = [...pool, currentChannelId];
  return [...new Set(ids.map((x) => String(x || "").trim()).filter(Boolean))];
}

/* ---------- which of them are on the board ---------- */

// Kept per viewed channel, not per account.
const SELECTION_PREFIX = "adm_lr_channels_";

export function readChannelSelection(currentChannelId) {
  try {
    const raw = localStorage.getItem(SELECTION_PREFIX + currentChannelId);
    if (!raw) return null;
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list.map(String) : null;
  } catch {
    return null;
  }
}

export function writeChannelSelection(currentChannelId, ids) {
  try {
    localStorage.setItem(SELECTION_PREFIX + currentChannelId, JSON.stringify(ids));
  } catch {
    /* private mode, or the quota is full — the board falls back to the pool */
  }
}

// The pool minus whatever the picker switched off. The viewed channel always
// stays in, and a stored list that no longer matches the account falls back to
// the whole pool rather than leaving the board blank.
export function selectedChannelIds(currentChannelId) {
  const pool = candidateChannelIds(currentChannelId);
  const chosen = readChannelSelection(currentChannelId);
  if (!chosen) return pool;

  const keep = new Set(chosen);
  const out = pool.filter((id) => keep.has(id) || id === currentChannelId);
  return out.length > 1 ? out : pool;
}

function todayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}

// The last minute with viewers, and when it was recorded: a stream that ended
// this morning still has a last value.
function latestFromMinutes(values, nowMinute) {
  if (!Array.isArray(values)) return { value: 0, minute: null };
  const upTo = Math.min(nowMinute, values.length - 1);
  for (let i = upTo; i >= 0; i--) {
    if (values[i] > 0) return { value: values[i], minute: i };
  }
  return { value: 0, minute: null };
}

// `channelIds` must be held in state by the caller: a fresh array every render
// would refetch the board every render. Omit it to use the stored selection.
export function useLiveRanking(channelId, refreshKey = 0, channelIds = null) {
  const [ids, setIds] = useState([]);
  const [infoById, setInfoById] = useState({});
  const [values, setValues] = useState({});
  const [nowMinute, setNowMinute] = useState(currentPanelMinute);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    const chosen = channelIds ?? selectedChannelIds(channelId);
    setIds(chosen.slice(0, MAX_CHANNELS));
  }, [channelId, channelIds]);

  useEffect(() => {
    const tick = setInterval(() => setNowMinute(currentPanelMinute()), RANKING_POLL_MS);
    return () => clearInterval(tick);
  }, []);

  /* ---------- names ---------- */

  // /ci is batched and abbreviated: i = id, n = name, u = logo.
  useEffect(() => {
    if (!ids.length) return;
    const ac = new AbortController();
    let alive = true;

    api
      .get(`/ci?i=${ids.map(encodeURIComponent).join(LIVE_SEPARATOR)}`, { signal: ac.signal })
      .then((res) => {
        if (!alive || !Array.isArray(res.data)) return;
        const map = {};
        for (const info of res.data) {
          const id = String(info?.i || "");
          if (id) map[id] = { name: (info.n || "").trim(), logo: info.u || "" };
        }
        setInfoById(map);
      })
      .catch(() => {});

    return () => {
      alive = false;
      ac.abort();
    };
  }, [ids]);

  /* ---------- live values ---------- */

  const fetchValues = useCallback(async (channelIds) => {
    if (!channelIds.length) {
      setValues({});
      return;
    }
    setLoading(true);

    const day = todayISO();
    const [liveRes, ytRes] = await Promise.allSettled([
      api.get(`/live?c=${channelIds.map(encodeURIComponent).join(LIVE_SEPARATOR)}`),
      api.get(
        `/api/get-youtube-tracker?c=${channelIds
          .map(encodeURIComponent)
          .join(TRACKER_SEPARATOR)}&start=${day}&end=${day}`
      ),
    ]);

    const minuteNow = currentPanelMinute();
    const next = {};
    for (const id of channelIds) next[id] = { tv: 0, yt: 0, ytMinute: null };

    if (liveRes.status === "fulfilled") {
      for (const [id, v] of Object.entries(liveRes.value.data || {})) {
        if (next[id]) next[id].tv = Number(v) || 0;
      }
    }
    if (ytRes.status === "fulfilled" && Array.isArray(ytRes.value.data)) {
      for (const row of ytRes.value.data) {
        const id = String(row.channelId || "");
        if (!next[id]) continue;
        const { value, minute } = latestFromMinutes(row.videoConsumption, minuteNow);
        next[id].yt = value;
        next[id].ytMinute = minute;
      }
    }

    setValues(next);
    setLastUpdated(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchValues(ids);
    const timer = setInterval(() => fetchValues(ids), RANKING_POLL_MS);
    return () => clearInterval(timer);
  }, [ids, refreshKey, fetchValues]);

  // A hidden tab's timer is throttled and the missed ticks are never made up,
  // so returning to the board reads rather than waiting out the interval.
  const refreshNow = useCallback(() => {
    setNowMinute(currentPanelMinute());
    fetchValues(ids);
  }, [ids, fetchValues]);

  useVisibleRefresh(refreshNow, ids.length > 0);

  /* ---------- ranking ---------- */

  const rows = useMemo(() => {
    const built = ids.map((id) => {
      const v = values[id] || { tv: 0, yt: 0, ytMinute: null };

      // An ended stream's last minute sits there unchanged, so it only counts
      // as current if it was recorded a moment ago.
      const ytAge = v.ytMinute === null ? null : nowMinute - v.ytMinute;
      const ytLive = ytAge !== null && ytAge >= 0 && ytAge <= LIVE_TOLERANCE_MINUTES;

      // One bar per channel: TV sets and YouTube tabs are counted by different
      // systems, so adding them would invent an audience.
      const useYoutube = (ytLive ? v.yt : 0) > v.tv;
      const value = useYoutube ? v.yt : v.tv;

      return {
        id,
        name: infoById[id]?.name || id,
        logo: infoById[id]?.logo || "",
        value,
        // With both sources at zero the comparison above falls to TV, which
        // would label a silent YouTube stream as something never measured.
        source: value > 0 ? (useYoutube ? "YT" : "TV") : "",
        offAir: value <= 0,
        lastSeen: v.ytMinute !== null ? hhmm(v.ytMinute) : "",
        isOwn: id === channelId,
      };
    });

    // Off air drops below everything live but stays on the board.
    built.sort((a, b) => {
      if (a.offAir !== b.offAir) return a.offAir ? 1 : -1;
      return b.value - a.value;
    });

    let rank = 0;
    built.forEach((row, i) => {
      row.rank = row.offAir ? null : ++rank;
      row.position = i + 1;
    });
    return built;
  }, [ids, values, infoById, nowMinute, channelId]);

  return { rows, ids, nowMinute, loading, lastUpdated };
}
